import React, { useCallback, useEffect, useMemo, useRef } from "react";
import ReactEditorKit from "react-text-editor-kit";
import { invokeApi } from "src/bl_libs/invokeApi";
import { s3baseUrl } from "src/config/config";
import { IMAGE_UPLOAD_PATH, TEXT_EDITOR_KIT_API_KEY } from "./editorKitConfig";

/**
 * EDITOR-KIT — one rich-text editor for the whole portal, built on
 * react-text-editor-kit. The old TinyMCE wrappers render this instead of
 * TinyMCE, so every screen that used TinyMCE keeps its props and data.
 *
 * Props
 *   value / onChange(html)   controlled HTML, same as before
 *   height                   number (px) or CSS string
 *   variant                  "full" (default toolbar) | "email" (short toolbar)
 *   onReady(api)             small TinyMCE-like handle for old callers:
 *                            getContent(), setContent(html), insertContent(html),
 *                            focus(), on("focus" | "blur", fn), getBody()
 *   onFocus / onBlur         optional
 *   uploadImage(file)        optional; default = S3 upload API, returns full URL
 */

const EMAIL_TOOLBAR = [
  "undo",
  "redo",
  "|",
  "format",
  "|",
  "bold",
  "italic",
  "underline",
  "textColor",
  "backgroundColor",
  "|",
  "alignment",
  "|",
  "orderedList",
  "unorderedList",
  "|",
  "link",
  "image",
  "|",
  "removeFormat",
];

const THEME = {
  "save-button-background": "#2e7d32",
};

// TinyMCE "height" was the whole box (toolbar + text). The kit's own
// height prop does not hold its toolbar, so the writing area gets the
// height instead: old height minus the toolbar, never below 150px.
const TOOLBAR_SPACE = { full: 150, email: 60 };
const contentHeight = (height, fallback, variant) => {
  const h = height || fallback;
  if (typeof h !== "number") return h;
  return `${Math.max(150, h - (TOOLBAR_SPACE[variant] || 150))}px`;
};

export const uploadImageToS3 = async (file) => {
  if (!file) return null;
  const formData = new FormData();
  formData.append("image", file);
  formData.append("width", "600");
  try {
    const res = await invokeApi({
      path: IMAGE_UPLOAD_PATH,
      method: "POST",
      headers: { "x-sh-auth": localStorage.getItem("token") },
      postData: formData,
    });
    if (res?.code === 200 && res.image_path) return s3baseUrl + res.image_path;
  } catch (_err) {
    // fall through
  }
  return null;
};

export default function KitEditor({
  value,
  onChange,
  height,
  variant = "full",
  placeholder,
  onReady,
  onFocus,
  onBlur,
  uploadImage,
  testId,
}) {
  const domRef = useRef(null); // the contentEditable element
  const rangeRef = useRef(null); // last caret position inside the editor
  const valueRef = useRef(value || "");
  const onChangeRef = useRef(onChange);
  const listenersRef = useRef({ focus: [], blur: [] });
  const readyRef = useRef(false);

  valueRef.current = value || "";
  onChangeRef.current = onChange;

  const getEl = () => domRef.current?.current || null;

  // Remember where the caret was, so insertContent() can go back there
  // after the user clicks a button outside the editor.
  useEffect(() => {
    const onSelection = () => {
      const el = getEl();
      const sel = window.getSelection();
      if (!el || !sel || !sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) rangeRef.current = range.cloneRange();
    };
    document.addEventListener("selectionchange", onSelection);
    return () => document.removeEventListener("selectionchange", onSelection);
  }, []);

  const placeCaret = useCallback(() => {
    const el = getEl();
    if (!el) return null;
    el.focus();
    const sel = window.getSelection();
    let range = rangeRef.current;
    if (!range || !el.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    return el;
  }, []);

  const api = useMemo(
    () => ({
      getContent: () => valueRef.current,
      setContent: (html) => onChangeRef.current && onChangeRef.current(html || ""),
      getBody: () => getEl(),
      focus: () => {
        placeCaret();
      },
      insertContent: (html) => {
        const el = placeCaret();
        if (!el) {
          if (onChangeRef.current) onChangeRef.current(`${valueRef.current}${html}`);
          return;
        }
        const ok = document.execCommand("insertHTML", false, html);
        if (!ok) el.insertAdjacentHTML("beforeend", html);
        // make sure the parent state has the new HTML even if the kit
        // did not see an input event
        if (onChangeRef.current) onChangeRef.current(el.innerHTML);
      },
      on: (event, fn) => {
        if (listenersRef.current[event] && typeof fn === "function") {
          listenersRef.current[event].push(fn);
        }
      },
    }),
    [placeCaret]
  );

  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // onReady(api) runs once, when the editor element first exists
  const getEditorRef = useCallback(
    (ref) => {
      domRef.current = ref;
      if (!readyRef.current && ref?.current) {
        readyRef.current = true;
        if (onReadyRef.current) onReadyRef.current(api);
      }
    },
    [api]
  );

  // focus / blur listeners (props + api.on)
  useEffect(() => {
    const el = getEl();
    if (!el) return undefined;
    const handleFocus = (e) => {
      if (onFocus) onFocus(e);
      listenersRef.current.focus.forEach((fn) => fn(e));
    };
    const handleBlur = (e) => {
      if (onBlur) onBlur(e);
      listenersRef.current.blur.forEach((fn) => fn(e));
    };
    el.addEventListener("focus", handleFocus);
    el.addEventListener("blur", handleBlur);
    return () => {
      el.removeEventListener("focus", handleFocus);
      el.removeEventListener("blur", handleBlur);
    };
  });

  const imageHandler = useCallback(
    async (inputs) => {
      const file = inputs?.image;
      if (!file || typeof file === "string") return file || null;
      return (uploadImage || uploadImageToS3)(file);
    },
    [uploadImage]
  );

  const handleChange = useCallback((html) => {
    if (onChangeRef.current) onChangeRef.current(html);
  }, []);

  return (
    <ReactEditorKit
      apiKey={TEXT_EDITOR_KIT_API_KEY}
      value={value || ""}
      onChange={handleChange}
      getEditorRef={getEditorRef}
      image_handler={imageHandler}
      placeholder={placeholder}
      style={{
        height: contentHeight(height, 400, variant),
        minHeight: 0,
        overflowY: "auto",
        boxSizing: "border-box",
      }}
      toolbar={variant === "email" ? EMAIL_TOOLBAR : undefined}
      navbar={variant === "email" ? [] : undefined}
      theme_config={THEME}
      enable_word_count={variant !== "email"}
      mainProps={{ "data-testid": testId || "kit-editor" }}
    />
  );
}
